import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { admin } from '../services/api';
import { AdminUser, AdminUsageItem, UsageTransaction, Pagination } from '../types/api';
import { useAuthContext } from '../contexts/AuthContext';
import './AdminPage.css';
import './admin/AdminShell.css';
import './connector/ConnectorShell.css';
import ConnectorPanel from './admin/ConnectorPanel';
import AdminUsageSection from './admin/usage/AdminUsageSection';
import { balanceTone } from '../utils/usageContract';
import './admin/usage/UsageBilling.css';
import NLTConfigPanel from './admin/NLTConfigPanel';
import AdminOnboarding from './AdminOnboarding';
import AdminTabBar from './admin/AdminTabBar';
import LeaderboardPanel from './admin/LeaderboardPanel';
import ConnectorSectionShell from './connector/ConnectorSectionShell';
import { resolveAdminTab, type AdminTabType } from './admin/adminTabConfig';
import { capitalizeError } from '../utils/errorUtils';
import { setStoredSelectedIntegrationId } from '../utils/selectedIntegrationStorage';
import { NOTIFICATION_AUTO_HIDE_MS } from '../constants/uiTiming';

type TabType = AdminTabType | 'usage-history';

const AdminPage: React.FC = () => {
  const USERS_PAGE_LIMIT = 50;
  const USAGE_PAGE_LIMIT = 50;
  // Guardrails: prevent sending amounts that will lose precision or be rejected by API.
  // We use MAX_SAFE_INTEGER to avoid silently rounding large integers on the client.
  const MAX_TOPUP_AMOUNT = Number.MAX_SAFE_INTEGER;
  const { user, isLoading } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const currentEmail = user?.email?.toLowerCase();
  const currentUserId = (user as any)?.id as string | undefined;
  
  const isAdminPath = location.pathname === '/admin';
  const resolvedTab = resolveAdminTab(location.pathname, location.search);
  const tabFromUrl = isAdminPath ? resolvedTab : 'users';

  // Все хуки должны быть вызваны до любых условных возвратов
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usage, setUsage] = useState<AdminUsageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const [usersFilters, setUsersFilters] = useState<{ q: string }>({ q: '' });
  const [usersTotal, setUsersTotal] = useState<number>(0);
  const [usersLoadingMore, setUsersLoadingMore] = useState(false);
  const [usersLoadMoreError, setUsersLoadMoreError] = useState<string | null>(null);
  const usersLoadMoreRef = useRef<HTMLDivElement | null>(null);

  const [usageTotal, setUsageTotal] = useState<number>(0);
  const [usageLoadingMore, setUsageLoadingMore] = useState(false);
  const [usageLoadMoreError, setUsageLoadMoreError] = useState<string | null>(null);
  const usageLoadMoreRef = useRef<HTMLDivElement | null>(null);

  // Billing / statistics (admin usage transactions)
  const [billingStats, setBillingStats] = useState<{
    items: UsageTransaction[];
    pagination: Pagination | null;
  } | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingLoaded, setBillingLoaded] = useState(false);
  const [billingPageSize, setBillingPageSize] = useState<number>(20);
  const [billingCurrentPage, setBillingCurrentPage] = useState<number>(1);

  // Модальное окно для редактирования пользователя
  const [editingUserRole, setEditingUserRole] = useState<{
    user_id: string;
    email: string;
    role: 'user' | 'admin';
  } | null>(null);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  const roleDropdownMenuRef = useRef<HTMLDivElement>(null);

  const [topupModal, setTopupModal] = useState<{
    user_id: string;
    email: string;
    amountText: string;
    reason: string;
  } | null>(null);
  const [topupAmountError, setTopupAmountError] = useState<string | null>(null);
  const [topupSubmitError, setTopupSubmitError] = useState<string | null>(null);
  const topupAmountInputRef = useRef<HTMLInputElement>(null);
  const [showAdminOnboarding, setShowAdminOnboarding] = useState(false);
  
  // Проверка роли - дополнительная защита на уровне компонента
  const userRole = (user as any)?.role;
  const isAdmin = typeof userRole === 'string' && userRole.toLowerCase() === 'admin';
  
  const normalizeAmountInput = (raw: string) => {
    // Support both "." and "," as decimal separators, normalize to ".".
    return raw.replace(',', '.');
  };

  const validateAmountText = (raw: string) => {
    const value = normalizeAmountInput(raw);

    if (value.trim() === '') return { normalized: '', error: null as string | null, parsed: NaN };

    // Allow only digits and a single decimal separator, up to 6 digits after separator.
    // Examples allowed: "12", "12.", "12.3", ".5" (we'll normalize to "0.5").
    const isValid = /^\d*([.]\d{0,6})?$/.test(value);
    if (!isValid) {
      return { normalized: value, error: 'Please enter a numeric amount', parsed: NaN };
    }

    const normalized = value.startsWith('.') ? `0${value}` : value;
    const parsed = Number(normalized);

    if (!Number.isFinite(parsed)) {
      return {
        normalized,
        error: `Amount exceeds maximum allowed value (max: ${formatNumber(MAX_TOPUP_AMOUNT)})`,
        parsed,
      };
    }
    if (parsed > MAX_TOPUP_AMOUNT) {
      return {
        normalized,
        error: `Amount exceeds maximum allowed value (max: ${formatNumber(MAX_TOPUP_AMOUNT)})`,
        parsed,
      };
    }
    if (parsed <= 0) {
      return { normalized, error: 'Amount must be a positive number', parsed };
    }

    return { normalized, error: null as string | null, parsed };
  };

  // Все useEffect должны быть до условных возвратов
  // Если загрузка завершена и пользователь не админ - редирект
  useEffect(() => {
    if (!isLoading && (!user || !isAdmin)) {
      navigate('/', { replace: true });
    }
  }, [isLoading, user, isAdmin, navigate]);

  // Sync activeTab from URL when on /admin so NavLink tabs stay in sync
  useEffect(() => {
    if (isAdminPath) setActiveTab(tabFromUrl);
  }, [isAdminPath, tabFromUrl]);

  useEffect(() => {
    if (activeTab === 'users' || activeTab === 'usage') {
      loadData();
    }
    // usage-history и connector обрабатываются внутри своих компонентов
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Lazy-load billing statistics when first opening the tab
  useEffect(() => {
    if (activeTab === 'billing' && !billingLoaded) {
      // Reset to page 1 on first load
      setBillingCurrentPage(1);
      setBillingPageSize(20);
      void loadBilling(1, 20);
    }
  }, [activeTab, billingLoaded]);

  const hasMoreUsers = usersTotal > 0 && users.length < usersTotal;
  const hasMoreUsage = usageTotal > 0 && usage.length < usageTotal;

  useEffect(() => {
    if (activeTab !== 'users') return;
    if (!hasMoreUsers) return;
    if (loading || usersLoadingMore) return;

    const node = usersLoadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        // Guard against rapid double-fires
        if (loading || usersLoadingMore) return;
        void loadMoreUsers();
      },
      { root: null, rootMargin: '250px 0px', threshold: 0.01 }
    );

    observer.observe(node);
    return () => observer.disconnect();
    // Intentionally depend on the high-level pagination state + filters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, hasMoreUsers, loading, usersLoadingMore, usersFilters.q, users.length]);

  useEffect(() => {
    if (activeTab !== 'usage') return;
    if (!hasMoreUsage) return;
    if (loading || usageLoadingMore) return;

    const node = usageLoadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (loading || usageLoadingMore) return;
        void loadMoreUsage();
      },
      { root: null, rootMargin: '250px 0px', threshold: 0.01 }
    );

    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, hasMoreUsage, loading, usageLoadingMore, usage.length]);

  /** Close role dropdown when clicking outside (menu is position:absolute inside modal, not fixed). */
  useEffect(() => {
    if (!isRoleDropdownOpen) return;

    const onDocPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      const inToggle = roleDropdownRef.current?.contains(target);
      const inMenu = roleDropdownMenuRef.current?.contains(target);
      if (!inToggle && !inMenu) setIsRoleDropdownOpen(false);
    };

    document.addEventListener('mousedown', onDocPointerDown);
    document.addEventListener('touchstart', onDocPointerDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown);
      document.removeEventListener('touchstart', onDocPointerDown);
    };
  }, [isRoleDropdownOpen]);

  /** Reset dropdown when edit modal closes or when editing own user (no dropdown). */
  useEffect(() => {
    if (!editingUserRole) {
      setIsRoleDropdownOpen(false);
      return;
    }
    const isSelf =
      Boolean(currentEmail) && editingUserRole.email.toLowerCase() === currentEmail;
    if (isSelf) setIsRoleDropdownOpen(false);
  }, [editingUserRole, currentEmail]);

  /** Escape closes whichever admin user modal is open. */
  useEffect(() => {
    if (!editingUserRole && !topupModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setIsRoleDropdownOpen(false);
      setEditingUserRole(null);
      setTopupModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingUserRole, topupModal]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      switch (activeTab) {
        case 'users':
          await loadUsers({ reset: true });
          break;
        case 'usage':
          await loadUsage({ reset: true });
          break;
        case 'connector':
        case 'usage-history':
        case 'billing':
          // handled inside corresponding panels
          break;
      }
    } catch (err: any) {
      setError(capitalizeError(err.message) || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadBilling = async (page?: number, pageSize?: number) => {
    const currentPage = page ?? billingCurrentPage;
    const currentPageSize = pageSize ?? billingPageSize;
    const offset = (currentPage - 1) * currentPageSize;

    setBillingLoading(true);
    setError(null);
    try {
      const response = await admin.getUsageStatistics({
        limit: currentPageSize,
        offset: offset,
      });
      if (response.success && response.data) {
        setBillingStats({
          items: response.data.items || [],
          pagination: response.data.pagination || null,
        });
        setBillingLoaded(true);
        setBillingCurrentPage(currentPage);
        setBillingPageSize(currentPageSize);
      } else {
        const message =
          capitalizeError(response.error?.message) || 'Failed to load billing statistics';
        setBillingStats(null);
        setBillingLoaded(false);
        setError(message);
      }
    } catch (err: any) {
      const message =
        capitalizeError(err?.response?.data?.error?.message || err.message) ||
        'Failed to load billing statistics';
      setBillingStats(null);
      setBillingLoaded(false);
      setError(message);
    } finally {
      setBillingLoading(false);
    }
  };

  const loadUsers = async (opts?: { reset?: boolean }) => {
    try {
      if (opts?.reset) {
        setUsers([]);
        setUsersTotal(0);
        setUsersLoadMoreError(null);
      }
      const response = await admin.getUsers({
        q: usersFilters.q || undefined,
        limit: USERS_PAGE_LIMIT,
        offset: 0,
      });
      if (response.success && response.data) {
        setUsers(response.data.items);
        setUsersTotal(response.data.pagination.total);
        setUsersLoadMoreError(null);
      }
    } catch (err: any) {
      setUsers([]);
      setUsersTotal(0);
      setError(capitalizeError(err?.response?.data?.error?.message || err.message) || 'Failed to load users');
    }
  };

  const loadMoreUsers = async () => {
    if (activeTab !== 'users') return;
    if (loading || usersLoadingMore) return;
    if (!hasMoreUsers) return;

    setUsersLoadingMore(true);
    setUsersLoadMoreError(null);

    try {
      // Use current list length as next offset to be resilient if limit changes or server behaves oddly.
      const nextOffset = users.length;
      const response = await admin.getUsers({
        q: usersFilters.q || undefined,
        limit: USERS_PAGE_LIMIT,
        offset: nextOffset,
      });

      if (response.success && response.data) {
        const nextItems = response.data.items || [];
        setUsersTotal(response.data.pagination.total);
        setUsersLoadMoreError(null);

        // De-dupe by id in case backend returns overlapping windows.
        setUsers((prev) => {
          if (nextItems.length === 0) return prev;
          const seen = new Set(prev.map((u) => u.id));
          const merged = [...prev];
          for (const u of nextItems) {
            if (seen.has(u.id)) continue;
            seen.add(u.id);
            merged.push(u);
          }
          return merged;
        });
      } else {
        setUsersLoadMoreError(capitalizeError(response.error?.message) || 'Failed to load more users');
      }
    } catch (err: any) {
      setUsersLoadMoreError(capitalizeError(err?.response?.data?.error?.message || err.message) || 'Failed to load more users');
    } finally {
      setUsersLoadingMore(false);
    }
  };

  const loadUsage = async (opts?: { reset?: boolean }) => {
    try {
      if (opts?.reset) {
        setUsage([]);
        setUsageTotal(0);
        setUsageLoadMoreError(null);
      }
      const params = {
        limit: USAGE_PAGE_LIMIT,
        offset: 0,
      };
      const response = await admin.getUsage(params);
      if (response.success && response.data) {
        setUsage(response.data.items);
        setUsageTotal(response.data.pagination.total);
        setUsageLoadMoreError(null);
      }
    } catch (err: any) {
      setUsage([]);
      setUsageTotal(0);
      setError(capitalizeError(err?.response?.data?.error?.message || err.message) || 'Failed to load usage');
    }
  };

  const loadMoreUsage = async () => {
    if (activeTab !== 'usage') return;
    if (loading || usageLoadingMore) return;
    if (!hasMoreUsage) return;

    setUsageLoadingMore(true);
    setUsageLoadMoreError(null);

    try {
      const nextOffset = usage.length;
      const response = await admin.getUsage({
        limit: USAGE_PAGE_LIMIT,
        offset: nextOffset,
      });

      if (response.success && response.data) {
        const nextItems = response.data.items || [];
        setUsageTotal(response.data.pagination.total);
        setUsageLoadMoreError(null);

        // De-dupe by user_id (usage is per user aggregate).
        setUsage((prev) => {
          if (nextItems.length === 0) return prev;
          const seen = new Set(prev.map((u) => u.user_id));
          const merged = [...prev];
          for (const u of nextItems) {
            if (seen.has(u.user_id)) continue;
            seen.add(u.user_id);
            merged.push(u);
          }
          return merged;
        });
      } else {
        setUsageLoadMoreError(capitalizeError(response.error?.message) || 'Failed to load more usage');
      }
    } catch (err: any) {
      setUsageLoadMoreError(capitalizeError(err?.response?.data?.error?.message || err.message) || 'Failed to load more usage');
    } finally {
      setUsageLoadingMore(false);
    }
  };

  const openEditUserModal = (user: AdminUser) => {
    setIsRoleDropdownOpen(false);
    setEditingUserRole({
      user_id: user.id,
      email: user.email,
      role: user.role === 'admin' ? 'admin' : 'user',
    });
  };

  const handleUpdateUserRole = async () => {
    if (!editingUserRole) return;
    // Дополнительная проверка роли перед выполнением админской операции
    if (!isAdmin) {
      setError('You do not have permission to perform this action');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const response = await admin.assignRole({
        user_id: editingUserRole.user_id,
        role: editingUserRole.role,
      });

      if (response.success) {
        setSuccessMessage('User role updated successfully');
        setEditingUserRole(null);
        await loadUsers();
        setTimeout(() => setSuccessMessage(null), NOTIFICATION_AUTO_HIDE_MS);
      } else {
        setError(capitalizeError(response.error?.message) || 'Failed to update user role');
      }
    } catch (err: any) {
      setError(capitalizeError(err.response?.data?.error?.message || err.message) || 'Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const openTopupModal = (user: AdminUser) => {
    setTopupAmountError(null);
    setTopupSubmitError(null);
    setTopupModal({
      user_id: user.id,
      email: user.email,
      amountText: '',
      reason: '',
    });
  };

  const handleTopup = async () => {
    if (!topupModal) return;
    // Дополнительная проверка роли перед выполнением админской операции
    if (!isAdmin) {
      setError('You do not have permission to perform this action');
      return;
    }

    // Validate user_id
    if (!topupModal.user_id || !topupModal.user_id.trim()) {
      setTopupSubmitError('User ID is required');
      return;
    }

    const { normalized, error } = validateAmountText(topupModal.amountText);
    const amountText = normalized.endsWith('.') ? normalized.slice(0, -1) : normalized;
    const amount = Number(amountText);

    if (error || !Number.isFinite(amount) || amount <= 0) {
      setTopupSubmitError(null);
      setTopupAmountError(error || 'Amount must be a positive number');
      topupAmountInputRef.current?.focus();
      return;
    }
    if (amount > MAX_TOPUP_AMOUNT) {
      setTopupSubmitError(null);
      setTopupAmountError(`Amount exceeds maximum allowed value (max: ${formatNumber(MAX_TOPUP_AMOUNT)})`);
      topupAmountInputRef.current?.focus();
      return;
    }

    setLoading(true);
    setTopupAmountError(null);
    setTopupSubmitError(null);

    try {
      // Ensure amount is a valid number (not NaN, not Infinity)
      if (!Number.isFinite(amount) || amount <= 0) {
        setTopupAmountError('Amount must be a valid positive number');
        topupAmountInputRef.current?.focus();
        return;
      }

      const response = await admin.topupUser(topupModal.user_id, {
        // Send as a number to match server expectations
        amount: amount,
        reason: topupModal.reason || undefined,
      });

      if (response.success) {
        setSuccessMessage('User balance updated successfully');
        setTopupModal(null);
        await loadUsers();
        setTimeout(() => setSuccessMessage(null), NOTIFICATION_AUTO_HIDE_MS);
      } else {
        const errMsg = response.error?.message || '';
        const isInvalidUserId = /invalid\s*(user\s*)?id|user\s*not\s*found|no\s*(such\s*)?user|invalid\s*user_id/i.test(errMsg.toLowerCase()) ||
          (errMsg.toLowerCase().includes('user') && (errMsg.toLowerCase().includes('invalid') || errMsg.toLowerCase().includes('not found')));
        setTopupSubmitError(
          isInvalidUserId
            ? 'The user ID you entered is incorrect. Please check the value and try again.'
            : (capitalizeError(response.error?.message) || 'Failed to update balance')
        );
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const responseData = err?.response?.data;
      
      // Check if response is HTML (like Google Docs page) - indicates wrong endpoint or redirect
      const isHtmlResponse = typeof responseData === 'string' && (
        responseData.includes('<!DOCTYPE') || 
        responseData.includes('<html') ||
        responseData.includes('Google Docs')
      );

      let serverMsg = responseData?.error?.message;
      
      // Try to extract error message from various response formats
      if (!serverMsg && responseData) {
        if (typeof responseData === 'string') {
          // If it's HTML, provide a specific error
          if (isHtmlResponse) {
            serverMsg = 'Server returned an unexpected response. The API endpoint may not be available.';
          } else {
            serverMsg = responseData;
          }
        } else if (responseData.message) {
          serverMsg = responseData.message;
        }
      }

      // User-friendly message when the error is about invalid / wrong user ID
      const invalidUserIdMessage = 'The user ID you entered is incorrect. Please check the value and try again.';
      const msgLower = (serverMsg || '').toLowerCase();
      const looksLikeInvalidUserId =
        status === 404 ||
        /invalid\s*(user\s*)?id|user\s*id\s*invalid|user\s*not\s*found|no\s*(such\s*)?user|unknown\s*user|invalid\s*user_id|user_id\s*invalid/i.test(msgLower) ||
        (msgLower.includes('user') && (msgLower.includes('invalid') || msgLower.includes('not found')));

      if (looksLikeInvalidUserId && !isHtmlResponse) {
        setTopupSubmitError(invalidUserIdMessage);
        return;
      }

      // 400 is almost always input validation - show it near the field instead of a generic banner.
      if (status === 400) {
        setTopupSubmitError(null);
        setTopupAmountError(capitalizeError(serverMsg) || 'Invalid amount. Please enter a numeric value.');
        topupAmountInputRef.current?.focus();
        return;
      }

      // 404 - endpoint not found (only if not already handled as invalid user id above)
      if (status === 404) {
        setTopupSubmitError(
          isHtmlResponse 
            ? 'API endpoint not found. The topup feature may not be available on this server.'
            : invalidUserIdMessage
        );
        return;
      }

      // 403 - forbidden
      if (status === 403) {
        setTopupSubmitError('You do not have permission to perform this action.');
        return;
      }

      // 500+ - server error
      if (status && status >= 500) {
        setTopupSubmitError('Server error. Please try again later.');
        return;
      }

      // Generic error
      setTopupSubmitError(
        capitalizeError(serverMsg || err.message) || 
        'Failed to update balance. Please check your input and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const isEditingRoleSelf = Boolean(
    editingUserRole && currentEmail && editingUserRole.email.toLowerCase() === currentEmail
  );

  const openAdminTour = () => setShowAdminOnboarding(true);

  const handleAdminTabChange = (tab: AdminTabType) => {
    if (tab === 'fine-tuning-graph') {
      sessionStorage.setItem('adminScrollY', String(window.scrollY));
      navigate('/admin/fine-tuning-graph');
      return;
    }
    if (tab === 'users') {
      navigate('/admin');
      return;
    }
    navigate(`/admin?tab=${tab}`);
  };

  return (
    <div className="admin-page admin-shell-page connector-shell-page">
      <AdminTabBar activeTab={resolvedTab} onTabChange={handleAdminTabChange} />

      <div className="admin-shell-body">
        {error && (
          <div className="admin-message error">
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}
        {successMessage && (
          <div className="admin-message success">
            <i className="fas fa-check-circle"></i> {successMessage}
          </div>
        )}

        <div className="admin-content">
          {loading && (activeTab === 'users' || activeTab === 'usage') ? (
            <div className="admin-loading">
              <div className="spinner"></div>
              <p>Loading...</p>
            </div>
          ) : (
            <>
              {activeTab === 'users' && (
                <ConnectorSectionShell
                  eyebrow="Accounts"
                  title={`Users (${users.length}${usersTotal > 0 ? ` / ${usersTotal}` : ''})`}
                  subtitle="Search accounts, edit roles, and top up token balances."
                  icon="fa-users"
                  onHelp={openAdminTour}
                  actions={
                    <>
                      <input
                        type="text"
                        placeholder="Search (email/name)"
                        className="filter-input"
                        value={usersFilters.q}
                        onChange={(e) => setUsersFilters({ q: e.target.value })}
                      />
                      <button
                        type="button"
                        onClick={() => loadUsers({ reset: true })}
                        className="connector-shell-btn connector-shell-btn--primary"
                      >
                        <i className="fas fa-search" aria-hidden="true" />
                        Search
                      </button>
                      <button
                        type="button"
                        onClick={() => loadUsers({ reset: true })}
                        className="connector-shell-btn"
                      >
                        <i className="fas fa-sync-alt" aria-hidden="true" />
                        Refresh
                      </button>
                    </>
                  }
                >
                  <div className="connector-section-panel connector-section-panel--flush" id="admin-users-section">
                    <div className="admin-table-wrapper" id="admin-users-table-wrap">
                    <table className="admin-table admin-table--users">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Email</th>
                          <th>Name</th>
                          <th>Company</th>
                          <th>Created</th>
                          <th>Role</th>
                          <th>Balance</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="empty-cell">
                              <div className="empty-state">
                                <div className="empty-title">No users found</div>
                                <div className="empty-subtitle">Try adjusting search or refresh.</div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          users.map((user) => (
                            <tr key={user.id}>
                              <td data-label="ID" className="id-cell" title={user.id}>{user.id}</td>
                              <td data-label="Email">{user.email}</td>
                              <td data-label="Name">{user.name}</td>
                              <td data-label="Company">{user.company_name || '-'}</td>
                              <td data-label="Created">{formatDate(user.created_at)}</td>
                              <td data-label="Role">
                                {user.role === 'admin' ? (
                                  <span className="badge badge-admin">Admin</span>
                                ) : (
                                  <span className="badge badge-user">User</span>
                                )}
                              </td>
                              <td data-label="Balance" className="tokens-cell">
                                <span
                                  className={`ub-balance ub-balance--${balanceTone(user.token_balance)}`}
                                  title={balanceTone(user.token_balance) === 'overdraft' ? 'Overdraft: spending beyond the balance is allowed' : undefined}
                                >
                                  {formatNumber(user.token_balance)}
                                </span>
                              </td>
                              <td data-label="Actions" className="actions-cell">
                                <div className="actions-wrap">
                                  <button
                                    onClick={() => openEditUserModal(user)}
                                    className="edit-btn"
                                  >
                                    <i className="fas fa-edit"></i> Edit
                                  </button>
                                  <button
                                    onClick={() => openTopupModal(user)}
                                    className="edit-btn"
                                  >
                                    <i className="fas fa-coins"></i> Add tokens
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Infinite scroll footer */}
                  {users.length > 0 && (
                    <div className="admin-infinite-footer">
                      {usersLoadingMore && (
                        <div className="admin-infinite-status" aria-live="polite">
                          <div className="spinner spinner--small" />
                          <span>Loading more users…</span>
                        </div>
                      )}

                      {!usersLoadingMore && usersLoadMoreError && (
                        <div className="admin-infinite-status admin-infinite-status--error" role="alert">
                          <span>{usersLoadMoreError}</span>
                          <button className="admin-infinite-retry" onClick={() => void loadMoreUsers()}>
                            Retry
                          </button>
                        </div>
                      )}

                      {!usersLoadingMore && !usersLoadMoreError && !hasMoreUsers && usersTotal > 0 && (
                        <div className="admin-infinite-status" aria-live="polite">
                          <span>All users loaded.</span>
                        </div>
                      )}

                      {/* Sentinel observed by IntersectionObserver */}
                      <div ref={usersLoadMoreRef} className="admin-infinite-sentinel" />
                    </div>
                  )}
                  </div>
                </ConnectorSectionShell>
              )}

              {activeTab === 'usage' && (
                <ConnectorSectionShell
                  eyebrow="Aggregates"
                  title={`Token usage (${usage.length}${usageTotal > 0 ? ` / ${usageTotal}` : ''})`}
                  subtitle="Per-user token totals and estimated costs."
                  icon="fa-chart-line"
                  onHelp={openAdminTour}
                  actions={
                    <button
                      type="button"
                      onClick={() => loadUsage({ reset: true })}
                      className="connector-shell-btn"
                    >
                      <i className="fas fa-sync-alt" aria-hidden="true" />
                      Refresh
                    </button>
                  }
                >
                  <div className="connector-section-panel connector-section-panel--flush" id="admin-usage-section">
                    <div className="admin-table-wrapper" id="admin-usage-table-wrap">
                    <table className="admin-table admin-table--usage">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>User ID</th>
                          <th>Balance</th>
                          <th>Prompt</th>
                          <th>Completion</th>
                          <th>Total</th>
                          <th>Total Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usage.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="empty-cell">
                              <div className="empty-state">
                                <div className="empty-title">No usage data</div>
                                <div className="empty-subtitle">No data available yet.</div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          usage.map((item) => (
                            <tr key={`${item.user_id}-${item.email}`}>
                              <td data-label="Email">{item.email}</td>
                              <td data-label="User ID" className="id-cell" title={item.user_id}>{item.user_id}</td>
                              <td data-label="Balance" className="tokens-cell">
                                <span
                                  className={`ub-balance ub-balance--${balanceTone(item.token_balance)}`}
                                  title={balanceTone(item.token_balance) === 'overdraft' ? 'Overdraft: spending beyond the balance is allowed' : undefined}
                                >
                                  {formatNumber(item.token_balance)}
                                </span>
                              </td>
                              <td data-label="Prompt" className="tokens-cell">{formatNumber(item.prompt_tokens)}</td>
                              <td data-label="Completion" className="tokens-cell">{formatNumber(item.completion_tokens)}</td>
                              <td data-label="Total" className="tokens-cell">{formatNumber(item.total_tokens)}</td>
                              <td data-label="Total Cost" className="tokens-cell">${(item.total_cost ?? 0).toFixed(6)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Infinite scroll footer */}
                  {usage.length > 0 && (
                    <div className="admin-infinite-footer">
                      {usageLoadingMore && (
                        <div className="admin-infinite-status" aria-live="polite">
                          <div className="spinner spinner--small" />
                          <span>Loading more usage…</span>
                        </div>
                      )}

                      {!usageLoadingMore && usageLoadMoreError && (
                        <div className="admin-infinite-status admin-infinite-status--error" role="alert">
                          <span>{usageLoadMoreError}</span>
                          <button className="admin-infinite-retry" onClick={() => void loadMoreUsage()}>
                            Retry
                          </button>
                        </div>
                      )}

                      {!usageLoadingMore && !usageLoadMoreError && !hasMoreUsage && usageTotal > 0 && (
                        <div className="admin-infinite-status" aria-live="polite">
                          <span>All usage loaded.</span>
                        </div>
                      )}

                      {/* Sentinel observed by IntersectionObserver */}
                      <div ref={usageLoadMoreRef} className="admin-infinite-sentinel" />
                    </div>
                  )}
                  </div>
                </ConnectorSectionShell>
              )}

              {activeTab === 'billing' && (
                <AdminUsageSection onOpenAdminTour={openAdminTour} />
              )}

              {/* Connector Tab */}
              {activeTab === 'connector' && (
                <ConnectorPanel
                  selectedIntegrationId={selectedIntegrationId}
                  onSelectIntegrationId={(id) => {
                    setSelectedIntegrationId(id);
                    setStoredSelectedIntegrationId('admin', currentUserId, id);
                  }}
                  currentUserId={currentUserId}
                  onOpenAdminTour={openAdminTour}
                />
              )}

              {/* NLT Configs Tab */}
              {activeTab === 'nlt' && (
                <NLTConfigPanel onOpenAdminTour={openAdminTour} />
              )}

              {activeTab === 'leaderboard' && <LeaderboardPanel />}
            </>
          )}
        </div>
      </div>

      {/* Edit User Role Modal */}
      {editingUserRole && (
        <div
          className="modal-overlay admin-user-modal-overlay"
          role="presentation"
        >
          <div
            className="modal-content admin-user-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-edit-user-title"
          >
            <div className="modal-header">
              <h3 id="admin-edit-user-title">Edit User</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsRoleDropdownOpen(false);
                  setEditingUserRole(null);
                }}
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Email</label>
                <input type="text" value={editingUserRole.email} disabled />
              </div>
              <div className="form-group">
                <label>Role</label>
                <div className="role-dropdown" ref={roleDropdownRef}>
                  <button
                    type="button"
                    className={`role-dropdown-toggle${isRoleDropdownOpen ? ' open' : ''}${isEditingRoleSelf ? ' disabled' : ''}`}
                    onClick={() => {
                      if (isEditingRoleSelf) return;
                      setIsRoleDropdownOpen((v) => !v);
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={isRoleDropdownOpen}
                    disabled={isEditingRoleSelf}
                    title={isEditingRoleSelf ? 'You cannot change your own role' : undefined}
                  >
                    <span className="role-dropdown-value">
                      {editingUserRole.role === 'admin' ? 'Admin' : 'User'}
                    </span>
                    <i className={`fas fa-chevron-down ${isRoleDropdownOpen ? 'rotated' : ''}`} />
                  </button>
                  {isRoleDropdownOpen && (
                    <div
                      ref={roleDropdownMenuRef}
                      className="role-dropdown-menu"
                      role="listbox"
                      aria-label="Role"
                    >
                      <button
                        type="button"
                        className={`role-dropdown-option${editingUserRole.role === 'user' ? ' active' : ''}`}
                        onClick={() => {
                          setEditingUserRole({ ...editingUserRole, role: 'user' });
                          setIsRoleDropdownOpen(false);
                        }}
                      >
                        User
                      </button>
                      <button
                        type="button"
                        className={`role-dropdown-option${editingUserRole.role === 'admin' ? ' active' : ''}`}
                        onClick={() => {
                          setEditingUserRole({ ...editingUserRole, role: 'admin' });
                          setIsRoleDropdownOpen(false);
                        }}
                      >
                        Admin
                      </button>
                    </div>
                  )}
                </div>
                {isEditingRoleSelf && (
                  <div className="form-hint-text" style={{ marginTop: 10 }}>
                    You can’t change your own role.
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setIsRoleDropdownOpen(false);
                  setEditingUserRole(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-save"
                onClick={handleUpdateUserRole}
                disabled={loading || isEditingRoleSelf}
                title={isEditingRoleSelf ? 'You cannot change your own role' : undefined}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Topup modal */}
      {topupModal && (
        <div
          className="modal-overlay admin-user-modal-overlay"
          role="presentation"
        >
          <div
            className="modal-content admin-user-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-topup-title"
          >
            <div className="modal-header">
              <h3 id="admin-topup-title">Add tokens</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={(e) => {
                  e.stopPropagation();
                  setTopupModal(null);
                }}
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </div>
            <div className="modal-body">
              {topupSubmitError && (
                <div className="modal-inline-error" role="alert">
                  <i className="fas fa-exclamation-circle"></i> {topupSubmitError}
                </div>
              )}
              <div className="form-group">
                <label>Email</label>
                <input type="text" value={topupModal.email} disabled />
              </div>
              <div className="form-group">
                <label>Amount</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="Enter amount"
                  value={topupModal.amountText}
                  ref={topupAmountInputRef}
                  className={topupAmountError ? 'input-error' : undefined}
                  onChange={(e) => {
                    const next = normalizeAmountInput(e.target.value);
                    // Allow empty
                    if (next === '') {
                      setTopupAmountError(null);
                      setTopupModal({ ...topupModal, amountText: '' });
                      return;
                    }

                    const { normalized, error: nextError } = validateAmountText(next);
                    // Always update the input value to show what user typed (filtered)
                    // But show error if validation fails
                    if (nextError) {
                      // Only block if it's clearly invalid (contains letters or special chars)
                      // Allow partial input like "1." or "." which will be validated on blur
                      const hasInvalidChars = /[^0-9.]/.test(next);
                      if (hasInvalidChars) {
                        // Block invalid characters completely
                        return;
                      }
                      // For partial valid input (like "1."), allow it but show error
                      setTopupModal({ ...topupModal, amountText: normalized });
                      setTopupAmountError(nextError);
                      return;
                    }

                    setTopupModal({ ...topupModal, amountText: normalized });
                    setTopupAmountError(null);
                  }}
                  onBlur={() => {
                    const current = topupModal.amountText;
                    if (!current) {
                      setTopupAmountError(null);
                      return;
                    }

                    const { normalized, error: nextError } = validateAmountText(current);
                    if (nextError) {
                      setTopupAmountError(nextError);
                      return;
                    }

                    // Clean up trailing dot
                    const cleaned = normalized.endsWith('.') ? normalized.slice(0, -1) : normalized;
                    if (cleaned !== topupModal.amountText) {
                      setTopupModal({ ...topupModal, amountText: cleaned });
                    }
                    // Clear error if value is valid
                    if (!nextError) {
                      setTopupAmountError(null);
                    }
                  }}
                />
                {topupAmountError && <div className="form-error-text">{topupAmountError}</div>}
                {!topupAmountError && (
                  <div className="form-hint-text">Maximum allowed amount: {formatNumber(MAX_TOPUP_AMOUNT)}</div>
                )}
              </div>
              <div className="form-group">
                <label>Reason (optional)</label>
                <input
                  type="text"
                  value={topupModal.reason}
                  onChange={(e) =>
                    setTopupModal({
                      ...topupModal,
                      reason: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={() => setTopupModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-save"
                onClick={handleTopup}
                disabled={
                  loading ||
                  !topupModal.amountText.trim() ||
                  Boolean(topupAmountError)
                }
              >
                {loading ? 'Saving...' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}

      <AdminOnboarding
        isOpen={showAdminOnboarding}
        onClose={() => setShowAdminOnboarding(false)}
        activeTab={activeTab}
      />
    </div>
  );
};

export default AdminPage;

